// URL <-> navigation-state mapping. The URL is the source of truth for
// where the user is (which view, which workspace, which home tab) so a
// refresh, a shared link, or back/forward all land in the same place —
// previously this lived only in React memory and every cold load reset to
// the home page (the "refresh on the map sends me home" bug).
//
// Route table:
//   /                  home page, Workspaces tab
//   /content           home page, Content tab
//   /map/<workspaceId> map viewer, landed/focused on that workspace
//   /callback          reserved for auth — handled by AuthContext BEFORE
//                      NavigationProvider ever mounts, never parsed here
//   anything else      treated as / (and the address bar is normalized)
//
// No react-router: three routes and no nested/dynamic segments beyond the
// workspace id don't justify the dependency; parseRoute/routePath are the
// whole "router".

// Workspace slugs are platform_db data (ROADMAP.md v0.6.2), fetched at runtime — this router
// can't know the valid set synchronously, so a workspace id is just a string here. Whether a
// given slug is a REAL, visible workspace is decided once WorkspacesContext's fetch resolves;
// MapView renders an honest "unknown workspace" state for a slug that turns out not to exist,
// same as it already does for the API being down entirely.
export type WorkspaceId = string;

export type HomeNav = 'workspaces' | 'content';

export type Route = { view: 'home'; nav: HomeNav } | { view: 'map'; workspace: WorkspaceId };

export const HOME_ROUTE: Route = { view: 'home', nav: 'workspaces' };

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return HOME_ROUTE;
  if (parts.length === 1 && parts[0] === 'content') return { view: 'home', nav: 'content' };
  if (parts.length === 2 && parts[0] === 'map' && parts[1].length > 0) {
    return { view: 'map', workspace: parts[1] };
  }
  return HOME_ROUTE;
}

export function routePath(route: Route): string {
  if (route.view === 'map') return `/map/${route.workspace}`;
  return route.nav === 'content' ? '/content' : '/';
}

/** True when `pathname` is exactly one of the routes above (so it's safe to
 * send someone back to it after login, for instance). */
export function isKnownPath(pathname: string): boolean {
  return routePath(parseRoute(pathname)) === pathname.replace(/\/+$/, '') || pathname === '/';
}
